// ── STATE ─────────────────────────────────────────────────────────────────
let curGroup = 'all';
let curCare = 'all';
let curRole = 'all';
let curSearch = '';

let curMode = 'library';
let curProblem = null;
let detailReturnMode = 'library';


// ── HELPERS ───────────────────────────────────────────────────────────────
const groupName = g => ({
  green: 'Green Algae',
  red: 'Red Algae',
  brown: 'Brown Algae',
  seagrass: 'Seagrass',
  cuc: 'Invertebrates',
  fish: 'Fish',
  blue: 'Blue Algae',
  purple: 'Coralline Algae',
  coral: 'Coral'
}[g] || g);

const cap = s => s ? s[0].toUpperCase() + s.slice(1) : '';

const lightLbl = l => ({
  low: 'Low light',
  moderate: 'Moderate light',
  high: 'High light'
}[l] || l);

const reefSafeLbl = r => {
  if (r === true) return 'Reef safe';
  if (r === 'caution') return 'Use with caution';
  return 'Not reef safe';
};


// ── SHOP HELPERS ──────────────────────────────────────────────────────────
const hasShopUrl = s => !!(s.shopUrl && s.shopUrl !== '#');
const hasShopItems = s => !!(s.shopItems && s.shopItems.length);


// ── REEF RX PROBLEM DEFINITIONS ───────────────────────────────────────────
const REEF_RX_PROBLEMS = {
  'cyano': {
    label: 'Red Slime / Cyanobacteria',
    icon: '🔴',
    grad: 'linear-gradient(150deg,#7B1520 0%,#B02030 60%,#3D0810 100%)',
    badgeBg: '#FDEAEC',
    badgeColor: '#7B1520',
    cause: 'A bacteria — not algae — that forms slimy red, maroon, or purple sheets with a distinctive musty smell. Caused by low flow, excess organics, or nutrient imbalance. Bubbles and peels off in sheets.',
    eduNote: '',
    recommended: []
  },

  'hair-algae': {
    label: 'Hair Algae',
    icon: '🌿',
    grad: 'linear-gradient(150deg,#1A4731 0%,#2D8A5F 60%,#0D2B1A 100%)',
    badgeBg: '#EAF4EE',
    badgeColor: '#1A4731',
    cause: 'Fast-growing filamentous algae that smothers rockwork and coral bases. Caused by high nutrients combined with insufficient herbivore pressure. Includes turf algae and film algae of similar origin.',
    eduNote: '',
    recommended: []
  },

  'bubble-algae': {
    label: 'Bubble Algae',
    icon: '🫧',
    grad: 'linear-gradient(150deg,#1A4731 0%,#2D8A5F 60%,#0D2B1A 100%)',
    badgeBg: '#EAF4EE',
    badgeColor: '#1A4731',
    cause: 'Valonia and related genera — shiny green bubbles that attach to rock and spread by releasing spores when ruptured. Introduced on live rock or coral frags. Remove manually before adding biological controls.',
    eduNote: '',
    recommended: []
  },

  'diatoms': {
    label: 'Diatoms / Brown Film',
    icon: '🟤',
    grad: 'linear-gradient(150deg,#5C3D11 0%,#A87830 60%,#2E1E08 100%)',
    badgeBg: '#FEF3E2',
    badgeColor: '#5C3D11',
    cause: 'Brown, dusty film coating sand, glass, and rock. Extremely common in new tanks as silicates leach from substrate. Almost always self-resolves within 4–12 weeks as the tank matures.',
    eduNote: '',
    recommended: []
  },

  'dinoflagellates': {
    label: 'Dinoflagellates / Dinos',
    icon: '🌡️',
    grad: 'linear-gradient(150deg,#5C3D11 0%,#A87830 60%,#2E1E08 100%)',
    badgeBg: '#FEF3E2',
    badgeColor: '#5C3D11',
    cause: 'Golden-brown stringy coating with visible trapped bubbles. Goes away at night and returns during the day — light-dependent behavior is the key identifier. Linked to ultra-low nutrient systems. Some species are toxic to livestock.',
    eduNote: 'Dinos have very limited livestock solutions — they are primarily a husbandry problem. Raising nutrients slightly, reducing photoperiod, and manual removal are the primary fixes. Livestock can apply grazing pressure but will not eliminate an outbreak on their own.',
    recommended: []
  },

  'nutrients': {
    label: 'High Nitrates & Phosphates',
    icon: '📈',
    grad: 'linear-gradient(150deg,#2D1B52 0%,#6A4DAA 60%,#160D29 100%)',
    badgeBg: '#EDE8F8',
    badgeColor: '#2D1B52',
    cause: 'Caused by overfeeding, overstocking, or insufficient export. Target nitrates under 10 ppm and phosphates under 0.1 ppm for reef systems. Both drive nuisance algae growth and place stress on corals.',
    eduNote: '',
    recommended: []
  },

  'aiptasia': {
    label: 'Aiptasia & Hydroids',
    icon: '⚡',
    grad: 'linear-gradient(150deg,#7B1520 0%,#B02030 60%,#3D0810 100%)',
    badgeBg: '#FDEAEC',
    badgeColor: '#7B1520',
    cause: 'Aiptasia are aggressive pest anemones introduced on live rock or coral frags. They sting nearby corals and spread rapidly. Hydroids are colonial cnidarians addressed by the same biological solutions.',
    eduNote: '',
    recommended: []
  },

  'flatworms': {
    label: 'Flatworms',
    icon: '🪱',
    grad: 'linear-gradient(150deg,#7B1520 0%,#B02030 60%,#3D0810 100%)',
    badgeBg: '#FDEAEC',
    badgeColor: '#7B1520',
    cause: 'Rust-brown oval specks (Convolutriloba) that multiply rapidly across rockwork and sand. Mostly harmless individually but release toxins when dying in large numbers — siphon out before treatment.',
    eduNote: '',
    recommended: []
  },

  'polyclad': {
    label: 'Polyclad Flatworms',
    icon: '🪱',
    grad: 'linear-gradient(150deg,#7B1520 0%,#B02030 60%,#3D0810 100%)',
    badgeBg: '#FDEAEC',
    badgeColor: '#7B1520',
    cause: 'Larger predatory flatworms that actively consume coral tissue, clam mantles, and other invertebrates. Distinct from common Convolutriloba — polyclads are a genuine emergency requiring immediate action.',
    eduNote: '',
    recommended: []
  },

  'aefw': {
    label: 'Acropora Flatworms (AEFW)',
    icon: '🦠',
    grad: 'linear-gradient(150deg,#7B1520 0%,#B02030 60%,#3D0810 100%)',
    badgeBg: '#FDEAEC',
    badgeColor: '#7B1520',
    cause: 'Amakusaplana acroporae — tiny flatworms living specifically on Acropora, consuming tissue and leaving white patches. Eggs appear as white clusters on coral branches. Highly destructive to SPS collections.',
    eduNote: '',
    recommended: []
  },

  'nudibranch-pests': {
    label: 'Pest Nudibranchs',
    icon: '🐌',
    grad: 'linear-gradient(150deg,#7B1520 0%,#B02030 60%,#3D0810 100%)',
    badgeBg: '#FDEAEC',
    badgeColor: '#7B1520',
    cause: 'Various nudibranch species that feed exclusively on specific corals — Montipora-eating, Zoanthid-eating, and others. Usually introduced on coral frags. Identified by white egg spirals on affected corals.',
    eduNote: '',
    recommended: []
  },

  'asterina': {
    label: 'Asterina Starfish',
    icon: '⭐',
    grad: 'linear-gradient(150deg,#5C3D11 0%,#A87830 60%,#2E1E08 100%)',
    badgeBg: '#FEF3E2',
    badgeColor: '#5C3D11',
    cause: 'Small, often asymmetric stars that multiply rapidly in reef tanks. Most populations are harmless but some individuals develop a taste for coral tissue and zoanthids. Population explosions are the main concern.',
    eduNote: '',
    recommended: []
  },

  'red-bugs': {
    label: 'Red Bugs',
    icon: '🔴',
    grad: 'linear-gradient(150deg,#7B1520 0%,#B02030 60%,#3D0810 100%)',
    badgeBg: '#FDEAEC',
    badgeColor: '#7B1520',
    cause: 'Tegastes acroporanus — tiny red parasites visible on Acropora as moving red dots. Cause polyp retraction and slow decline. The only reliable treatment is Interceptor (milbemycin oxime) — no livestock solution exists.',
    eduNote: 'Red bugs have no known livestock solutions and are treated with medication only. This page is provided for identification and education.',
    recommended: []
  },

  'sundial-snails': {
    label: 'Sundial Snails',
    icon: '🐚',
    grad: 'linear-gradient(150deg,#7B1520 0%,#B02030 60%,#3D0810 100%)',
    badgeBg: '#FDEAEC',
    badgeColor: '#7B1520',
    cause: 'Architectonica perspectiva — small patterned snails that feed exclusively on zoanthids and palythoa. Introduced on zoanthid frags. Very difficult to spot as they hide under the coral during the day.',
    eduNote: '',
    recommended: []
  },

  'vermetid-snails': {
    label: 'Vermetid Snails',
    icon: '🐚',
    grad: 'linear-gradient(150deg,#7B1520 0%,#B02030 60%,#3D0810 100%)',
    badgeBg: '#FDEAEC',
    badgeColor: '#7B1520',
    cause: 'Sessile snails that build irregular calcified tubes on rock and coral. They cast mucus webs to catch food — these webs irritate and damage nearby coral polyps, especially LPS. Manual removal is the primary fix.',
    eduNote: '',
    recommended: []
  },

  'bristleworms': {
    label: 'Bristleworms',
    icon: '🪱',
    grad: 'linear-gradient(150deg,#5C3D11 0%,#A87830 60%,#2E1E08 100%)',
    badgeBg: '#FEF3E2',
    badgeColor: '#5C3D11',
    cause: 'Most bristleworms are beneficial detritivores, but large populations of predatory species can damage coral and invertebrates. Traps and predators help control numbers.',
    eduNote: '',
    recommended: []
  },

  'detritus': {
    label: 'Detritus & Waste Buildup',
    icon: '🧹',
    grad: 'linear-gradient(150deg,#0C3B50 0%,#2490C0 60%,#061E29 100%)',
    badgeBg: '#E0F3FB',
    badgeColor: '#0C3B50',
    cause: 'Accumulation of uneaten food, fish waste, and organic matter in low-flow areas and the sandbed. Drives high nutrients, hydrogen sulfide in deep dead zones, and provides fuel for nuisance algae and cyano.',
    eduNote: '',
    recommended: []
  }
};


// ── SVG PLACEHOLDERS ─────────────────────────────────────────────────────
const groupMeta = {
  green: {
    bg: '#1A4731',
    mid: '#2D6A4F',
    light: '#A8D5B5',
    label: 'Green Algae',
    icon: `
      <line x1="32" y1="70" x2="32" y2="20" stroke="#A8D5B5" stroke-width="3" stroke-linecap="round"/>
      <path d="M32 55 Q20 48 14 36" stroke="#A8D5B5" stroke-width="2.5" fill="none"/>
      <path d="M32 55 Q44 48 50 36" stroke="#A8D5B5" stroke-width="2.5" fill="none"/>
    `
  },

  red: {
    bg: '#7B1829',
    mid: '#A82840',
    light: '#F4A8B5',
    label: 'Red Algae',
    icon: `
      <line x1="32" y1="70" x2="32" y2="20" stroke="#F4A8B5" stroke-width="3"/>
      <path d="M32 55 Q20 48 14 36" stroke="#F4A8B5" stroke-width="2.5" fill="none"/>
      <path d="M32 55 Q44 48 50 36" stroke="#F4A8B5" stroke-width="2.5" fill="none"/>
    `
  },

  brown: {
    bg: '#5C3D11',
    mid: '#8B6020',
    light: '#F0C878',
    label: 'Brown Algae',
    icon: `
      <line x1="32" y1="70" x2="32" y2="20" stroke="#F0C878" stroke-width="3"/>
      <path d="M32 55 Q20 48 14 36" stroke="#F0C878" stroke-width="2.5" fill="none"/>
      <path d="M32 55 Q44 48 50 36" stroke="#F0C878" stroke-width="2.5" fill="none"/>
    `
  },

  seagrass: {
    bg: '#0C3B50',
    mid: '#1878A0',
    light: '#90D4F0',
    label: 'Seagrass',
    icon: `
      <line x1="32" y1="70" x2="32" y2="20" stroke="#90D4F0" stroke-width="3"/>
      <path d="M32 55 Q20 48 14 36" stroke="#90D4F0" stroke-width="2.5" fill="none"/>
      <path d="M32 55 Q44 48 50 36" stroke="#90D4F0" stroke-width="2.5" fill="none"/>
    `
  },

  blue: {
    bg: '#0A2A5E',
    mid: '#1A4FA0',
    light: '#90B8F8',
    label: 'Blue-Green Algae',
    icon: `
      <line x1="32" y1="70" x2="32" y2="20" stroke="#90B8F8" stroke-width="3"/>
      <path d="M32 55 Q20 48 14 36" stroke="#90B8F8" stroke-width="2.5" fill="none"/>
      <path d="M32 55 Q44 48 50 36" stroke="#90B8F8" stroke-width="2.5" fill="none"/>
    `
  },

  purple: {
    bg: '#2D1B52',
    mid: '#5A3D90',
    light: '#C8B0F0',
    label: 'Purple / Coralline',
    icon: `
      <line x1="32" y1="70" x2="32" y2="20" stroke="#C8B0F0" stroke-width="3"/>
      <path d="M32 55 Q20 48 14 36" stroke="#C8B0F0" stroke-width="2.5" fill="none"/>
      <path d="M32 55 Q44 48 50 36" stroke="#C8B0F0" stroke-width="2.5" fill="none"/>
    `
  },

  cuc: {
    bg: '#1A2A3A',
    mid: '#2E4A6A',
    light: '#A8C8E8',
    label: 'Invertebrates',
    icon: `
      <circle cx="32" cy="38" r="16" stroke="#A8C8E8" stroke-width="2.5" fill="none"/>
      <circle cx="32" cy="38" r="6" stroke="#A8C8E8" stroke-width="1.5" fill="none"/>
    `
  },

  fish: {
    bg: '#0B4A6E',
    mid: '#1A7BA0',
    light: '#A8DCEF',
    label: 'Fish',
    icon: `
      <path d="M12 38 Q25 22 44 38 Q25 54 12 38Z" fill="none" stroke="#A8DCEF" stroke-width="2.5"/>
      <path d="M44 38 L55 28 L55 48 Z" fill="none" stroke="#A8DCEF" stroke-width="2.5"/>
      <circle cx="20" cy="35" r="2" fill="#A8DCEF"/>
    `
  },

  coral: {
    bg: '#6B2020',
    mid: '#A03030',
    light: '#F4B8B8',
    label: 'Coral',
    icon: `
      <path d="M32 65 L32 38" stroke="#F4B8B8" stroke-width="3"/>
      <path d="M32 42 Q20 36 18 24 Q24 24 28 30 Q28 22 32 18 Q36 22 36 30 Q40 24 46 24 Q44 36 32 42z"
        stroke="#F4B8B8" stroke-width="2" fill="none"/>
    `
  }
};


function makePlaceholder(s, h) {
  const m = groupMeta[s.group] || groupMeta.green;
  const sci = (s.scientificName || '').replace(/</g, '&lt;');

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg"
         width="100%"
         height="${h}"
         viewBox="0 0 400 ${h}"
         preserveAspectRatio="xMidYMid slice">

      <defs>
        <radialGradient id="rg${s.id}" cx="30%" cy="35%">
          <stop offset="0%" stop-color="${m.mid}" stop-opacity="0.9"/>
          <stop offset="100%" stop-color="${m.bg}"/>
        </radialGradient>

        <pattern id="dots${s.id}" width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="10" cy="10" r="1" fill="${m.light}" opacity="0.12"/>
        </pattern>
      </defs>

      <rect width="400" height="${h}" fill="url(#rg${s.id})"/>
      <rect width="400" height="${h}" fill="url(#dots${s.id})"/>

      <g transform="translate(${400 - h * 0.55},${h * 0.5 - 34}) scale(${h * 0.0085})">
        ${m.icon}
      </g>

      <rect x="0" y="${h - 52}" width="400" height="52" fill="${m.bg}" opacity="0.72"/>

      <text x="16" y="${h - 32}"
            font-family="Georgia,serif"
            font-size="15"
            fill="${m.light}"
            font-style="italic">${sci}</text>

      <text x="16" y="${h - 13}"
            font-family="Arial,sans-serif"
            font-size="11"
            fill="${m.light}"
            opacity="0.75"
            font-weight="500"
            letter-spacing="0.5">${m.label.toUpperCase()}</text>
    </svg>
  `;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}


// ── FILTER ────────────────────────────────────────────────────────────────
function getFiltered() {
  return SP.filter(s => {

    if (curGroup === 'cleanup') {
      if (!s.cleanupCrew) return false;
    } else if (curGroup !== 'all' && s.group !== curGroup) {
      return false;
    }

    if (curCare !== 'all' && s.careLevel !== curCare) {
      return false;
    }

    if (curRole !== 'all') {

      if (curGroup === 'cleanup') {
        if (curRole === 'fish') {
          if (s.group !== 'fish') return false;
        } else {
          if (s.cucType !== curRole) return false;
        }
      }

      else if (s.group === 'cuc') {
        if (s.cucType !== curRole) return false;
      }

      else if (s.group === 'coral') {
        if (s.coralType !== curRole) return false;
      }

      else {
        if (!s.roles || !s.roles.includes(curRole)) return false;
      }
    }

    if (curSearch) {
      const q = curSearch.toLowerCase();

      const searchable = [
        s.commonName,
        s.scientificName,
        s.description
      ].filter(Boolean).join(' ').toLowerCase();

      if (!searchable.includes(q)) return false;
    }

    return true;
  });
}


// ── CARD IMAGE ────────────────────────────────────────────────────────────
function imgTag(s, h) {
  const placeholder = makePlaceholder(s, h);
  const src = s.img || placeholder;

  const altSrc =
    s.img && s.img.endsWith('.jpeg')
      ? s.img.replace('.jpeg', '.jpg')
      : null;

  const onerr = altSrc
    ? `if(this.dataset.tried){
        this.onerror=null;
        this.src=makePlaceholder(SP.find(x=>x.id==='${s.id}'),${h});
      } else {
        this.dataset.tried=1;
        this.src='${altSrc}';
      }`
    : `if(!this.dataset.tried){
        this.onerror=null;
        this.dataset.tried=1;
        this.src=makePlaceholder(SP.find(x=>x.id==='${s.id}'),${h});
      }`;

  const cardImgPositionClass =
    s.id === 'garden-eel'
      ? ' card-img-position-top'
      : '';

  return `
    <img
      class="card-img${cardImgPositionClass}"
      src="${src}"
      alt="${s.commonName}"
      loading="lazy"
      style="height:${h}px;width:100%;object-fit:cover;display:block"
      onerror="${onerr}"
    >
  `;
}


// ── CARD ──────────────────────────────────────────────────────────────────
function renderCard(s) {

  let stats = '';

  if (s.group === 'cuc') {
    stats = `
      <span class="cstat-sm">${cap(s.cucType)}</span>
      <span class="cstat-sm">${reefSafeLbl(s.reefSafe)}</span>
      <span class="cstat-sm">Min ${s.minTankSize || '—'}gal</span>
    `;
  }

  else if (s.group === 'coral') {
    stats = `
      <span class="cstat-sm">${cap(s.coralType) || 'Coral'}</span>
      <span class="cstat-sm">${lightLbl(s.lighting)}</span>
      <span class="cstat-sm">${cap(s.careLevel)}</span>
    `;
  }

  else {
    stats = `
      <span class="cstat-sm">${lightLbl(s.lighting)}</span>
      <span class="cstat-sm">${cap(s.growthRate)} growth</span>
      ${
        s.roles
          ? `<span class="cstat-sm">${
              s.roles.map(r => ({
                'nutrient-export': 'Nutrient export',
                feeding: 'Feeding',
                display: 'Display',
                refugium: 'Refugium'
              }[r] || r)).join(' · ')
            }</span>`
          : ''
      }
    `;
  }


  const alsoRow =
    s.shopAlso && s.shopAlso.length
      ? `
        <div class="shop-also-row" onclick="event.stopPropagation()">
          Also sold as:
          ${s.shopAlso.map(a =>
            `<a href="${a.url}" target="_blank" rel="noopener">${a.label} →</a>`
          ).join(' ')}
        </div>
      `
      : '';


  const relRow =
    s.shopItems && s.shopItems.length
      ? `
        <div class="related-items-row">
          <span class="rir-label">🔗 Similar items in stock</span>
          ${s.shopItems.map(i =>
            `<a
              href="${i.url}"
              target="_blank"
              rel="noopener"
              onclick="event.stopPropagation()"
            >${i.label} →</a>`
          ).join('')}
        </div>
      `
      : '';


  return `
    <div class="species-card" data-id="${s.id}">

      <div class="card-img-wrap">
        ${imgTag(s, 175)}
      </div>

      ${alsoRow}

      <div class="card-body">

        <span class="card-badge badge-${s.group}">
          ${groupName(s.group)}
        </span>

        <div class="card-name-row">

          <div class="card-common">
            ${s.commonName}
          </div>

          ${
            hasShopUrl(s)
              ? `
                <a
                  class="shop-cart-btn"
                  href="${s.shopUrl}"
                  target="_blank"
                  rel="noopener"
                  onclick="event.stopPropagation()"
                  title="Buy in store"
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6"></path>
                  </svg>
                </a>
              `
              : ''
          }

        </div>

        <div class="card-sci">
          ${s.scientificName || ''}
        </div>

        <div class="card-desc">
          ${s.description || ''}
        </div>

        ${relRow}

      </div>

      <div class="card-footer">

        <div class="card-stats-row">

          ${stats}

          ${
            s.cleanupCrew === true
              ? '<span class="cuc-badge">🧹 Cleanup Crew</span>'
              : ''
          }

        </div>

        <span class="card-learn-more">
          Learn more
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7"></path>
          </svg>
        </span>

      </div>

    </div>
  `;
}


// ── DETAIL ────────────────────────────────────────────────────────────────
function renderDetail(s) {

  const gc = {
    green: {
      top: 'var(--g-mid)',
      badge: 'var(--g-light)',
      badgeText: 'var(--g-dark)',
      grad: 'var(--g-grad)'
    },

    red: {
      top: 'var(--r-mid)',
      badge: 'var(--r-light)',
      badgeText: 'var(--r-dark)',
      grad: 'var(--r-grad)'
    },

    brown: {
      top: 'var(--b-mid)',
      badge: 'var(--b-light)',
      badgeText: 'var(--b-dark)',
      grad: 'var(--b-grad)'
    },

    seagrass: {
      top: 'var(--s-mid)',
      badge: 'var(--s-light)',
      badgeText: 'var(--s-dark)',
      grad: 'var(--s-grad)'
    },

    cuc: {
      top: 'var(--c-mid)',
      badge: 'var(--c-light)',
      badgeText: 'var(--c-dark)',
      grad: 'var(--c-grad)'
    },

    fish: {
      top: 'var(--f-mid)',
      badge: 'var(--f-light)',
      badgeText: 'var(--f-dark)',
      grad: 'var(--f-grad)'
    },

    blue: {
      top: '#1A4FA0',
      badge: '#E8F0FC',
      badgeText: '#1A4FA0',
      grad: 'linear-gradient(135deg,#1A4FA0,#3B7DD8)'
    },

    purple: {
      top: '#6B3FA0',
      badge: '#F0EAFA',
      badgeText: '#6B3FA0',
      grad: 'linear-gradient(135deg,#6B3FA0,#9B6DC5)'
    },

    coral: {
      top: 'var(--coral-mid)',
      badge: 'var(--coral-light)',
      badgeText: 'var(--coral-dark)',
      grad: 'var(--coral-grad)'
    }

  }[s.group] || {
    badge: '#F0EFF8',
    badgeText: '#4A4E69'
  };


  const placeholder2 = makePlaceholder(s, 320);

  const altSrc2 =
    s.img && s.img.endsWith('.jpeg')
      ? s.img.replace('.jpeg', '.jpg')
      : null;


  const heroOnerr = altSrc2
    ? `if(this.dataset.tried){
        this.onerror=null;
        this.src=makePlaceholder(SP.find(x=>x.id==='${s.id}'),320);
      } else {
        this.dataset.tried=1;
        this.src='${altSrc2}';
      }`
    : `if(!this.dataset.tried){
        this.onerror=null;
        this.dataset.tried=1;
        this.src=makePlaceholder(SP.find(x=>x.id==='${s.id}'),320);
      }`;


  const heroImgPositionClass =
    s.id === 'garden-eel'
      ? ' detail-hero-img-position-top'
      : '';


  const heroImg = `
    <img
      class="detail-hero-img${heroImgPositionClass}"
      src="${s.img || placeholder2}"
      alt="${s.commonName}"
      onerror="${heroOnerr}"
      style="width:100%;height:320px;object-fit:cover;display:block"
    >
  `;


  let quickStats = '';

  if (s.group === 'cuc') {

    quickStats = `
      <div class="dq">
        <span class="dq-label">Type</span>
        <span class="dq-value">${cap(s.cucType)}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Reef Safe</span>
        <span class="dq-value">${reefSafeLbl(s.reefSafe)}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Dwelling</span>
        <span class="dq-value">${cap(s.dwelling)}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Min. Tank</span>
        <span class="dq-value">${s.minTankSize || '—'} gal</span>
      </div>

      <div class="dq">
        <span class="dq-label">Care Level</span>
        <span class="dq-value">${cap(s.careLevel)}</span>
      </div>
    `;
  }

  else if (s.group === 'coral') {

    quickStats = `
      <div class="dq">
        <span class="dq-label">Type</span>
        <span class="dq-value">${cap(s.coralType) || 'Coral'}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Care Level</span>
        <span class="dq-value">${cap(s.careLevel)}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Lighting</span>
        <span class="dq-value">${lightLbl(s.lighting)}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Flow</span>
        <span class="dq-value">${cap(s.flow)}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Reef Safe</span>
        <span class="dq-value">${reefSafeLbl(s.reefSafe)}</span>
      </div>
    `;
  }

  else {

    quickStats = `
      <div class="dq">
        <span class="dq-label">Care Level</span>
        <span class="dq-value">${cap(s.careLevel)}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Lighting</span>
        <span class="dq-value">${lightLbl(s.lighting)}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Flow</span>
        <span class="dq-value">${cap(s.flow)}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Growth</span>
        <span class="dq-value">${cap(s.growthRate)}</span>
      </div>

      <div class="dq">
        <span class="dq-label">Reef Safe</span>
        <span class="dq-value">${reefSafeLbl(s.reefSafe)}</span>
      </div>
    `;
  }


  let roleList = '';

  if (
    s.group === 'cuc' &&
    s.diet &&
    Array.isArray(s.diet.tankRole)
  ) {
    roleList = s.diet.tankRole.map(r => `<li>${r}</li>`).join('');
  }

  else if (Array.isArray(s.roles)) {
    roleList = s.roles.map(r => `<li>${r}</li>`).join('');
  }


  const compatClass =
    s.reefSafe === 'caution'
      ? 'compat-caution'
      : 'compat-safe';


  const noteBlock = s.note
    ? `
      <div class="note-block">
        <strong>📌 Taxonomic note:</strong>
        ${s.note}
      </div>
    `
    : '';


  let detailShop = '';

  if (hasShopUrl(s)) {

    const alsoLinks =
      s.shopAlso && s.shopAlso.length
        ? `
          <p style="margin-top:10px;font-size:13px;color:var(--text3)">
            Also sold as:
            ${
              s.shopAlso.map(a =>
                `<a
                  href="${a.url}"
                  target="_blank"
                  rel="noopener"
                  style="color:#1a6b42;font-weight:600"
                >${a.label}</a>`
              ).join(', ')
            }
          </p>
        `
        : '';


    detailShop += `
      <div class="detail-shop">

        <div>
          <h3>Available at Macroalgae Farms</h3>

          <p>
            Shop this species in our store — aquacultured stock with a
            live arrival guarantee.
          </p>

          ${alsoLinks}
        </div>

        <div class="shop-btns">
          <a
            href="${s.shopUrl}"
            target="_blank"
            rel="noopener"
            class="btn btn-dark"
          >
            🛒 Shop Now →
          </a>
        </div>

      </div>
    `;
  }


  if (hasShopItems(s)) {

    const relLinks = s.shopItems.map(i => `
      <a
        href="${i.url}"
        target="_blank"
        rel="noopener"
        class="btn btn-outline"
        style="font-size:13px"
      >
        ${i.label} →
      </a>
    `).join('');


    detailShop += `
      <div class="detail-shop">

        <div>
          <h3>Similar Items at Macroalgae Farms</h3>

          <p>
            We don't sell this exact species, but these closely related
            items are currently in stock.
          </p>
        </div>

        <div class="shop-btns">
          ${relLinks}
        </div>

      </div>
    `;
  }


  const t = s.taxonomy || {};

  const taxChain = Object.entries(t)
    .map(([rank, name]) =>
      `<span class="taxon-tag">${cap(rank)}: ${name}</span>`
    )
    .join('');


  const wp = s.waterParams || {};

  const wpRows = Object.entries(wp)
    .filter(([k]) => k !== 'notes')
    .map(([k, v]) =>
      `<li><strong>${cap(k)}:</strong> ${v}</li>`
    )
    .join('');


  const wpNotes = wp.notes
    ? `<p style="margin-top:10px"><strong>Notes:</strong> ${wp.notes}</p>`
    : '';


  return `

    <div class="detail-hero-card">

      ${heroImg}

      <div class="detail-hero-body">

        <span
          class="detail-grp-badge"
          style="background:${gc.badge};color:${gc.badgeText}"
        >
          ${groupName(s.group)}
        </span>

        <div
          style="
            display:flex;
            align-items:flex-start;
            gap:12px;
            flex-wrap:wrap;
          "
        >

          <div
            class="detail-common"
            style="flex:1"
          >
            ${s.commonName}
          </div>

          ${
            hasShopUrl(s)
              ? `
                <a
                  class="shop-cart-btn"
                  href="${s.shopUrl}"
                  target="_blank"
                  rel="noopener"
                  title="Buy in store"
                  style="
                    width:40px!important;
                    height:40px;
                    flex-shrink:0;
                    margin-top:4px;
                  "
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6"></path>
                  </svg>
                </a>
              `
              : ''
          }

        </div>

        <div class="detail-sci">
          ${s.scientificName || ''}
        </div>

        <div class="detail-quick">
          ${quickStats}
        </div>

        <p class="detail-intro">
          ${s.fullDesc || s.description || ''}
        </p>

        ${
          s.para2
            ? `
              <p class="detail-intro-label">
                🪸 In the Aquarium
              </p>

              <p
                class="detail-intro"
                style="
                  margin-top:6px;
                  border-left-color:var(--border2);
                "
              >
                ${s.para2}
              </p>
            `
            : ''
        }

        ${noteBlock}

        <div class="edu-panel">

          <div class="edu-grid">

            <div class="edu-card">
              <h4>🔬 Taxonomy</h4>
              <div class="taxon-chain">${taxChain}</div>
            </div>

            <div class="edu-card">
              <h4>🌊 Natural Habitat</h4>

              <p>${s.habitat || '—'}</p>

              ${
                s.distribution
                  ? `<p><strong>Distribution:</strong> ${s.distribution}</p>`
                  : ''
              }

            </div>

            <div class="edu-card">
              <h4>💧 Water Parameters</h4>

              <ul>${wpRows}</ul>

              ${wpNotes}
            </div>

            <div class="edu-card">
              <h4>💡 Did You Know?</h4>

              <p>${s.funFact || '—'}</p>
            </div>

          </div>

        </div>

      </div>

    </div>


    <div class="detail-content-grid">

      <div class="d-card">

        <h3>
          ${s.group === 'cuc' ? '🍽️ Diet' : '🌿 Tank Role'}
        </h3>

        <ul>
          ${roleList}
        </ul>

        <span class="compat-pill ${compatClass}">
          ${reefSafeLbl(s.reefSafe)}
        </span>

        <p style="margin-top:12px;font-size:13px">
          ${s.compatibility || ''}
        </p>

      </div>


      <div class="d-card">

        <h3>
          ${s.group === 'cuc' ? '🪸 Care & Husbandry' : '🪸 Aquarium Care'}
        </h3>

        <p>
          ${s.careNotes || ''}
        </p>

      </div>


      ${
        s.group !== 'cuc'
          ? `
            <div class="d-card">
              <h3>✂️ Propagation</h3>
              <p>${s.propagation || '—'}</p>
            </div>
          `
          : ''
      }

    </div>

    ${detailShop}
  `;
}


// ── RENDER LIBRARY ────────────────────────────────────────────────────────
function renderLib() {

  const filtered = getFiltered();
  const grid = document.getElementById('species-grid');

  if (!grid) return;


  if (!filtered.length) {

    grid.innerHTML = `
      <div class="no-results">
        <h3>No species found</h3>
        <p>Try adjusting your filters or search.</p>
      </div>
    `;
  }

  else {

    grid.innerHTML = filtered
      .map(renderCard)
      .join('');


    grid
      .querySelectorAll('.species-card')
      .forEach(c => {

        c.addEventListener('click', () => {
          detailReturnMode = 'library';
          showDetail(c.dataset.id);
        });

      });
  }


  const rc = document.getElementById('results-count');

  if (rc) {
    rc.textContent = `${filtered.length} species`;
  }
}


// ── COUNTS ────────────────────────────────────────────────────────────────
function updateCounts() {

  [
    'all',
    'green',
    'red',
    'brown',
    'seagrass',
    'blue',
    'purple',
    'cuc',
    'fish',
    'coral'
  ].forEach(g => {

    const el = document.getElementById(`count-${g}`);

    if (!el) return;

    const count =
      g === 'all'
        ? SP.length
        : SP.filter(s => s.group === g).length;

    el.textContent = `${count} species`;
  });


  const cleanupEl =
    document.getElementById('count-cleanup');

  if (cleanupEl) {
    cleanupEl.textContent =
      `${SP.filter(s => s.cleanupCrew).length} species`;
  }


  const algaeEl =
    document.getElementById('stat-algae');

  if (algaeEl) {
    algaeEl.textContent =
      SP.filter(
        s =>
          s.group !== 'cuc' &&
          s.group !== 'fish' &&
          s.group !== 'coral'
      ).length;
  }


  const cucEl =
    document.getElementById('stat-cuc');

  if (cucEl) {
    cucEl.textContent =
      SP.filter(s => s.group === 'cuc').length;
  }


  const fishEl =
    document.getElementById('stat-fish');

  if (fishEl) {
    fishEl.textContent =
      SP.filter(s => s.group === 'fish').length;
  }


  const coralEl =
    document.getElementById('stat-coral');

  if (coralEl) {
    coralEl.textContent =
      SP.filter(s => s.group === 'coral').length;
  }


  updateProblemCounts();
}


// ── PROBLEM COUNTS ────────────────────────────────────────────────────────
function getProblemIds() {

  if (
    typeof PROBLEMS_DATA !== 'undefined' &&
    Array.isArray(PROBLEMS_DATA)
  ) {
    return PROBLEMS_DATA.map(p => p.id);
  }

  return Object.keys(REEF_RX_PROBLEMS);
}


function getProblemData(id) {

  if (
    typeof PROBLEMS_DATA !== 'undefined' &&
    Array.isArray(PROBLEMS_DATA)
  ) {

    const found =
      PROBLEMS_DATA.find(p => p.id === id);

    if (found) {
      return found;
    }
  }

  return REEF_RX_PROBLEMS[id] || null;
}


function updateProblemCounts() {

  getProblemIds().forEach(id => {

    const prob = getProblemData(id);
    const allIds = prob && prob.species
      ? prob.species
      : [];


    let count = 0;

    if (allIds.length) {
      count = allIds.length;
    }

    else {
      count =
        SP.filter(
          s =>
            s.problems &&
            s.problems.includes(id)
        ).length;
    }


    const el =
      document.getElementById(`pcount-${id}`);

    if (el) {
      el.textContent =
        `${count}${count === 1 ? ' solution' : ' solutions'}`;
    }
  });
}


// ── SHOW / HIDE VIEWS ─────────────────────────────────────────────────────
function showDetail(id) {

  const s =
    SP.find(x => x.id === id);

  if (!s) return;


  const detailContent =
    document.getElementById('detail-content');

  const libraryView =
    document.getElementById('library-view');

  const rxView =
    document.getElementById('reef-rx-view');

  const detailView =
    document.getElementById('detail-view');


  if (detailContent) {
    detailContent.innerHTML = renderDetail(s);
  }

  if (libraryView) {
    libraryView.classList.add('hidden');
  }

  if (rxView) {
    rxView.classList.remove('active');
  }

  if (detailView) {
    detailView.classList.add('active');
  }


  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}


function showLib() {

  const detailView =
    document.getElementById('detail-view');

  const libraryView =
    document.getElementById('library-view');

  const rxView =
    document.getElementById('reef-rx-view');


  if (detailView) {
    detailView.classList.remove('active');
  }


  if (detailReturnMode === 'reef-rx') {

    if (libraryView) {
      libraryView.classList.add('hidden');
    }

    if (rxView) {
      rxView.classList.add('active');
    }


    setModeButtonState('reef-rx');

    detailReturnMode = 'library';
  }

  else {

    if (libraryView) {
      libraryView.classList.remove('hidden');
    }
  }
}


// ── MODE BUTTON STATE ─────────────────────────────────────────────────────
//
// IMPORTANT:
//
// Your current HTML does NOT contain data-mode attributes.
// These helpers intentionally use the existing IDs:
//
//   #pill-library
//   #pill-rx
//
// That means you do NOT have to modify your HTML toggle.
//
function setModeButtonState(mode) {

  const libraryPill =
    document.getElementById('pill-library');

  const rxPill =
    document.getElementById('pill-rx');


  if (libraryPill) {
    libraryPill.classList.toggle(
      'active',
      mode === 'library'
    );
  }


  if (rxPill) {
    rxPill.classList.toggle(
      'active',
      mode === 'reef-rx'
    );
  }
}


// ── MODE SWITCHING ────────────────────────────────────────────────────────
function switchMode(mode) {

  if (
    mode !== 'library' &&
    mode !== 'reef-rx'
  ) {
    return;
  }


  curMode = mode;


  const libraryView =
    document.getElementById('library-view');

  const reefRxView =
    document.getElementById('reef-rx-view');

  const detailView =
    document.getElementById('detail-view');

  const stickyBar =
    document.getElementById('sticky-bar');

  const heroSection =
    document.getElementById('hero-section');

  const heroLibContent =
    document.getElementById('hero-library-content');

  const heroRxContent =
    document.getElementById('hero-rx-content');


  // Make sure detail view is closed whenever
  // the main Library / Problem Catalog toggle is used.
  if (detailView) {
    detailView.classList.remove('active');
  }


  setModeButtonState(mode);


  if (mode === 'library') {

    if (libraryView) {
      libraryView.classList.remove('hidden');
    }

    if (reefRxView) {
      reefRxView.classList.remove('active');
    }

    if (heroSection) {
      heroSection.classList.remove('rx-mode');
    }

    if (heroLibContent) {
      heroLibContent.style.display = '';
    }

    if (heroRxContent) {
      heroRxContent.style.display = 'none';
    }


    if (stickyBar) {

      const catGrid =
        document.getElementById('cat-grid');

      if (catGrid) {

        const rect =
          catGrid.getBoundingClientRect();

        stickyBar.classList.toggle(
          'visible',
          rect.bottom < 0
        );
      }
    }
  }


  else {

    if (libraryView) {
      libraryView.classList.add('hidden');
    }

    if (reefRxView) {
      reefRxView.classList.add('active');
    }

    if (heroSection) {
      heroSection.classList.add('rx-mode');
    }

    if (heroLibContent) {
      heroLibContent.style.display = 'none';
    }

    if (heroRxContent) {
      heroRxContent.style.display = '';
    }

    if (stickyBar) {
      stickyBar.classList.remove('visible');
    }
  }
}


// ── REEF RX: SELECT PROBLEM ───────────────────────────────────────────────
function selectProblem(problemId) {

  const prob =
    getProblemData(problemId);

  if (!prob) return;


  curProblem = problemId;


  document
    .querySelectorAll('.problem-card')
    .forEach(c =>
      c.classList.remove('active')
    );


  const activeCard =
    document.querySelector(
      `.problem-card[data-problem="${problemId}"]`
    );

  if (activeCard) {
    activeCard.classList.add('active');
  }


  const gridSection =
    document.getElementById('problem-grid-section');

  if (gridSection) {
    gridSection.style.display = 'none';
  }


  const panel =
    document.getElementById('solution-panel');

  if (panel) {
    panel.classList.add('active');
  }


  const bannerEl =
    document.getElementById('solution-hero-banner');


  if (bannerEl) {

    if (prob.img) {

      bannerEl.style.background =
        prob.grad ||
        'linear-gradient(150deg,#7B1520,#B02030)';

      bannerEl.style.backgroundSize = 'cover';
      bannerEl.style.backgroundPosition = 'center';
      bannerEl.textContent = '';


      let bannerImg =
        bannerEl.querySelector('img.banner-photo');


      if (!bannerImg) {

        bannerImg =
          document.createElement('img');

        bannerImg.className = 'banner-photo';

        bannerImg.style.cssText = `
          position:absolute;
          inset:0;
          width:100%;
          height:100%;
          object-fit:cover;
        `;

        bannerEl.style.position = 'relative';
        bannerEl.style.overflow = 'hidden';

        bannerEl.insertBefore(
          bannerImg,
          bannerEl.firstChild
        );
      }


      bannerImg.src = prob.img;


      bannerImg.onerror = function() {
        bannerImg.remove();
      };
    }


    else {

      bannerEl.style.background =
        prob.grad ||
        'linear-gradient(150deg,#7B1520,#B02030)';


      const old =
        bannerEl.querySelector('img.banner-photo');

      if (old) {
        old.remove();
      }


      bannerEl.textContent =
        prob.icon || '';
    }
  }


  const badgeEl =
    document.getElementById('solution-badge');

  if (badgeEl) {

    badgeEl.textContent =
      `Problem Catalog — ${prob.label}`;

    badgeEl.style.background =
      prob.badgeBg || '#FDEAEC';

    badgeEl.style.color =
      prob.badgeColor || '#7B1520';
  }


  const nameEl =
    document.getElementById('solution-name');

  if (nameEl) {
    nameEl.textContent = prob.label;
  }


  const causeEl =
    document.getElementById('solution-cause');

  if (causeEl) {
    causeEl.textContent = prob.cause || '';
  }


  const harmContainer =
    document.getElementById('solution-harm');

  if (harmContainer) {

    if (prob.harm) {

      harmContainer.innerHTML =
        `<span style="margin-right:6px">💀</span>${prob.harm}`;

      harmContainer.style.display =
        'block';
    }

    else {
      harmContainer.style.display =
        'none';
    }
  }


  const eduNote =
    document.getElementById('solution-edu-note');

  if (eduNote) {

    if (prob.eduNote) {

      eduNote.textContent =
        prob.eduNote;

      eduNote.style.display =
        'block';
    }

    else {
      eduNote.style.display =
        'none';
    }
  }


  const recIds =
    prob.recommended || [];

  const allIds =
    prob.species || [];


  const recSpecies =
    recIds
      .map(id =>
        SP.find(s => s.id === id)
      )
      .filter(Boolean);


  const recIdSet =
    new Set(
      recSpecies.map(s => s.id)
    );


  let remainingMatches;


  if (allIds.length) {

    remainingMatches =
      allIds
        .map(id =>
          SP.find(s => s.id === id)
        )
        .filter(Boolean)
        .filter(
          s => !recIdSet.has(s.id)
        );
  }

  else {

    remainingMatches =
      SP.filter(
        s =>
          s.problems &&
          s.problems.includes(problemId) &&
          !recIdSet.has(s.id)
      );
  }


  const recGrid =
    document.getElementById('solution-rec-grid');


  if (recGrid) {

    if (recSpecies.length) {

      recGrid.innerHTML =
        recSpecies
          .map(renderCard)
          .join('');


      recGrid
        .querySelectorAll('.species-card')
        .forEach(c => {

          c.addEventListener(
            'click',
            () => {

              detailReturnMode =
                'reef-rx';

              showDetail(
                c.dataset.id
              );
            }
          );

        });
    }

    else {

      recGrid.innerHTML = `
        <div class="solution-empty">
          <p>
            No species tagged for this problem yet —
            check back as we continue building out the library.
          </p>
        </div>
      `;
    }
  }


  const allLabel =
    document.getElementById('solution-all-label');

  const allGrid =
    document.getElementById('solution-all-grid');


  if (remainingMatches.length) {

    if (allLabel) {

      allLabel.textContent =
        `All species that help (${remainingMatches.length} more)`;

      allLabel.style.display = '';
    }


    if (allGrid) {

      allGrid.innerHTML =
        remainingMatches
          .map(renderCard)
          .join('');


      allGrid
        .querySelectorAll('.species-card')
        .forEach(c => {

          c.addEventListener(
            'click',
            () => {

              detailReturnMode =
                'reef-rx';

              showDetail(
                c.dataset.id
              );
            }
          );

        });
    }
  }

  else {

    if (allLabel) {
      allLabel.style.display = 'none';
    }

    if (allGrid) {
      allGrid.innerHTML = '';
    }
  }


  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}


// ── REEF RX: CLEAR PROBLEM ────────────────────────────────────────────────
function clearProblem() {

  curProblem = null;


  document
    .querySelectorAll('.problem-card')
    .forEach(c =>
      c.classList.remove('active')
    );


  const gridSection =
    document.getElementById('problem-grid-section');

  if (gridSection) {
    gridSection.style.display = '';
  }


  const panel =
    document.getElementById('solution-panel');

  if (panel) {
    panel.classList.remove('active');
  }


  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}


// ── ROLE PILLS PER GROUP ──────────────────────────────────────────────────
function updateRolePills() {

  const rp =
    document.getElementById('role-pills');

  const rl =
    document.getElementById('role-label');


  if (!rp || !rl) return;


  if (curGroup === 'cleanup') {

    rl.textContent = 'Type';

    rp.innerHTML = `
      <span class="fpill active" data-filter="role" data-value="all">All</span>
      <span class="fpill" data-filter="role" data-value="snail">Snail</span>
      <span class="fpill" data-filter="role" data-value="crab">Crab</span>
      <span class="fpill" data-filter="role" data-value="shrimp">Shrimp</span>
      <span class="fpill" data-filter="role" data-value="urchin">Urchin</span>
      <span class="fpill" data-filter="role" data-value="star">Sea Star</span>
      <span class="fpill" data-filter="role" data-value="fish">Fish</span>
    `;
  }


  else if (curGroup === 'cuc') {

    rl.textContent = 'Type';

    rp.innerHTML = `
      <span class="fpill active" data-filter="role" data-value="all">All</span>
      <span class="fpill" data-filter="role" data-value="snail">Snail</span>
      <span class="fpill" data-filter="role" data-value="crab">Crab</span>
      <span class="fpill" data-filter="role" data-value="urchin">Urchin</span>
      <span class="fpill" data-filter="role" data-value="shrimp">Shrimp</span>
      <span class="fpill" data-filter="role" data-value="star">Sea Star</span>
      <span class="fpill" data-filter="role" data-value="conch">Conch</span>
    `;
  }


  else if (curGroup === 'coral') {

    rl.textContent = 'Type';

    rp.innerHTML = `
      <span class="fpill active" data-filter="role" data-value="all">All</span>
      <span class="fpill" data-filter="role" data-value="sps">SPS</span>
      <span class="fpill" data-filter="role" data-value="lps">LPS</span>
      <span class="fpill" data-filter="role" data-value="soft">Soft Coral</span>
    `;
  }


  else {

    rl.textContent = 'Role';

    rp.innerHTML = `
      <span class="fpill active" data-filter="role" data-value="all">All</span>
      <span class="fpill" data-filter="role" data-value="nutrient-export">Nutrient Export</span>
      <span class="fpill" data-filter="role" data-value="display">Display</span>
      <span class="fpill" data-filter="role" data-value="feeding">Feeding</span>
      <span class="fpill" data-filter="role" data-value="refugium">Refugium</span>
    `;
  }


  curRole = 'all';

  bindPills();
}


// ── EVENTS ────────────────────────────────────────────────────────────────
function bindPills() {

  document
    .querySelectorAll('.fpill')
    .forEach(p => {

      p.addEventListener(
        'click',
        function() {

          const f =
            this.dataset.filter;

          const v =
            this.dataset.value;


          const holder =
            this.closest('.filter-pills');

          if (holder) {

            holder
              .querySelectorAll('.fpill')
              .forEach(x =>
                x.classList.remove('active')
              );
          }


          this.classList.add('active');


          if (f === 'care') {
            curCare = v;
          }

          if (f === 'role') {
            curRole = v;
          }


          renderLib();
        }
      );

    });
}


// ── Problem card image injection ──────────────────────────────────────────
function injectProblemImages() {

  if (
    typeof PROBLEMS_DATA === 'undefined' ||
    !Array.isArray(PROBLEMS_DATA)
  ) {
    return;
  }


  PROBLEMS_DATA.forEach(prob => {

    if (!prob.img) return;


    const card =
      document.querySelector(
        `.problem-card[data-problem="${prob.id}"]`
      );

    if (!card) return;


    const wrap =
      card.querySelector('.card-img-wrap');

    if (!wrap) return;


    const img =
      document.createElement('img');


    img.src = prob.img;
    img.alt = prob.label;
    img.loading = 'lazy';

    img.style.cssText = `
      width:100%;
      height:100%;
      object-fit:cover;
      display:block;
    `;


    img.onerror = function() {

      wrap.style.display =
        'flex';

      wrap.style.alignItems =
        'center';

      wrap.style.justifyContent =
        'center';

      wrap.style.fontSize =
        '52px';

      wrap.innerHTML =
        prob.icon || '';
    };


    wrap.style.background =
      '#111';

    wrap.style.padding =
      '0';

    wrap.style.overflow =
      'hidden';

    wrap.innerHTML =
      '';

    wrap.appendChild(img);
  });
}


// ── INIT ──────────────────────────────────────────────────────────────────
function init() {

  injectProblemImages();


  // Core library
  updateCounts();
  renderLib();
  bindPills();


  // Ensure initial toggle appearance
  setModeButtonState('library');


  // ── Category cards ──
  const catGrid =
    document.getElementById('cat-grid');


  if (catGrid) {

    catGrid.addEventListener(
      'click',
      e => {

        const card =
          e.target.closest('.cat-card');

        if (!card) return;


        document
          .querySelectorAll('.cat-card')
          .forEach(c =>
            c.classList.remove('active')
          );


        card.classList.add('active');


        curGroup =
          card.dataset.group || 'all';

        curCare =
          'all';


        document
          .querySelectorAll('[data-filter="care"]')
          .forEach(p => {

            p.classList.remove('active');

            if (
              p.dataset.value === 'all'
            ) {
              p.classList.add('active');
            }

          });


        updateRolePills();
        renderLib();
      }
    );
  }


  // ── Sticky category buttons ──
  document
    .querySelectorAll(
      '#sticky-icons .sticky-icon-btn'
    )
    .forEach(btn => {

      btn.addEventListener(
        'click',
        () => {

          const group =
            btn.dataset.group;


          const catCard =
            document.querySelector(
              `.cat-card[data-group="${group}"]`
            );


          if (catCard) {
            catCard.click();
          }


          document
            .querySelectorAll(
              '#sticky-icons .sticky-icon-btn'
            )
            .forEach(b =>
              b.classList.remove('active')
            );


          btn.classList.add('active');
        }
      );

    });


  // ── Main search ──
  const searchInput =
    document.getElementById('search-input');


  if (searchInput) {

    searchInput.addEventListener(
      'input',
      function() {

        curSearch =
          this.value.trim();

        renderLib();
      }
    );
  }


  // ── Detail back ──
  const detailBack =
    document.getElementById('detail-back');


  if (detailBack) {

    detailBack.addEventListener(
      'click',
      showLib
    );
  }


  // ================================================================
  // MODE TOGGLE
  //
  // FIXED:
  // Uses your existing IDs instead of missing data-mode attributes.
  // ================================================================

  const libraryPill =
    document.getElementById('pill-library');

  const rxPill =
    document.getElementById('pill-rx');


  if (libraryPill) {

    libraryPill.addEventListener(
      'click',
      () => {

        if (curMode === 'library') {
          return;
        }

        switchMode('library');
      }
    );
  }


  if (rxPill) {

    rxPill.addEventListener(
      'click',
      () => {

        if (curMode === 'reef-rx') {
          return;
        }

        switchMode('reef-rx');
      }
    );
  }


  // ── Reef Rx problem cards ──
  const problemGrid =
    document.getElementById('problem-grid');


  if (problemGrid) {

    problemGrid.addEventListener(
      'click',
      e => {

        const card =
          e.target.closest('.problem-card');

        if (!card) return;


        const problemId =
          card.dataset.problem;

        if (!problemId) return;


        selectProblem(problemId);
      }
    );
  }


  // ── Reef Rx solution back button ──
  const solutionBack =
    document.getElementById(
      'solution-back-btn'
    );


  if (solutionBack) {

    solutionBack.addEventListener(
      'click',
      clearProblem
    );
  }


  // ── Sticky bar ──
  (() => {

    const stickyBar =
      document.getElementById('sticky-bar');

    const cg =
      document.getElementById('cat-grid');

    const stickyInput =
      document.getElementById(
        'sticky-search-input'
      );

    const mainInput =
      document.getElementById(
        'search-input'
      );


    if (
      !stickyBar ||
      !cg ||
      !stickyInput ||
      !mainInput
    ) {
      return;
    }


    function checkStickyBar() {

      if (curMode !== 'library') {

        stickyBar
          .classList
          .remove('visible');

        return;
      }


      const rect =
        cg.getBoundingClientRect();


      stickyBar.classList.toggle(
        'visible',
        rect.bottom < 0
      );
    }


    window.addEventListener(
      'scroll',
      checkStickyBar,
      { passive: true }
    );


    let el =
      cg.parentElement;


    while (
      el &&
      el !== document.documentElement
    ) {

      const ov =
        window
          .getComputedStyle(el)
          .overflowY;


      if (
        ov === 'auto' ||
        ov === 'scroll'
      ) {

        el.addEventListener(
          'scroll',
          checkStickyBar,
          { passive: true }
        );
      }


      el =
        el.parentElement;
    }


    checkStickyBar();


    stickyInput.addEventListener(
      'input',
      function() {

        mainInput.value =
          this.value;

        mainInput.dispatchEvent(
          new Event('input')
        );
      }
    );


    mainInput.addEventListener(
      'input',
      function() {

        if (
          document.activeElement !==
          stickyInput
        ) {
          stickyInput.value =
            this.value;
        }
      }
    );


    const stickyIcons =
      document.getElementById(
        'sticky-icons'
      );


    if (stickyIcons) {

      stickyIcons.addEventListener(
        'click',
        function(e) {

          const btn =
            e.target.closest(
              '.sticky-icon-btn'
            );

          if (!btn) return;


          const catCard =
            document.querySelector(
              `#cat-grid .cat-card[data-group="${btn.dataset.group}"]`
            );


          if (catCard) {
            catCard.click();
          }
        }
      );
    }


    cg.addEventListener(
      'click',
      function(e) {

        const card =
          e.target.closest('.cat-card');

        if (!card) return;


        document
          .querySelectorAll(
            '.sticky-icon-btn'
          )
          .forEach(b =>
            b.classList.remove('active')
          );


        const match =
          document.querySelector(
            `#sticky-icons .sticky-icon-btn[data-group="${card.dataset.group}"]`
          );


        if (match) {
          match.classList.add('active');
        }
      }
    );

  })();
}


// ── START ─────────────────────────────────────────────────────────────────
if (document.readyState === 'loading') {

  document.addEventListener(
    'DOMContentLoaded',
    init
  );

} else {

  init();
}